/**
 * =============================================================
 * AADHAV ACADEMY – ADMIN STUDENTS API
 * Role: Admin
 * Scope: Tenant-based student management
 * Standard: Enterprise / MNC Level
 * =============================================================
 */

import { apiCall } from "../core/apiCall";

/* =============================================================
   BASE ENDPOINT
============================================================= */
const ADMIN_STUDENTS_ENDPOINT = "/admin/students/index.php";

/* =============================================================
   GET – Fetch Students (Admin View)
   Supports: pagination, search
============================================================= */
export const getAdminStudents = async (
  { page = 1, limit = 10, search = "" } = {},
  options = {}
) => {
  try {
    const res = await apiCall(
      "GET",
      ADMIN_STUDENTS_ENDPOINT,
      null,
      "Admin – Fetch Students",
      {
        params: { page, limit, search },
        ...options,
      }
    );

    if (!res || res.status !== true) {
      return {
        success: false,
        message: res?.message || "Failed to fetch students",
        data: [],
        meta: {},
      };
    }

    return {
      success: true,
      data: res.data || [],
      meta: res.meta || {
        total: 0,
        page: 1,
        limit,
        total_pages: 0,
      },
    };
  } catch (error) {
    console.error("Admin Fetch Students Error:", error);

    return {
      success: false,
      message: error.message || "Unexpected error occurred",
      data: [],
      meta: {},
    };
  }
};

/* =============================================================
   GET – Fetch Single Student
============================================================= */
export const getAdminStudentById = async (id, options = {}) => {
  try {
    const res = await apiCall(
      "GET",
      `${ADMIN_STUDENTS_ENDPOINT}/${id}`,
      null,
      "Admin – Fetch Student By ID",
      options
    );

    if (!res || res.status !== true) {
      return {
        success: false,
        message: res?.message || "Failed to fetch student",
      };
    }

    return {
      success: true,
      data: res.data,
    };
  } catch (error) {
    console.error("Admin Fetch Student By ID Error:", error);

    return {
      success: false,
      message: error.message || "Unexpected error occurred",
    };
  }
};

/* =============================================================
   POST – Create Student
============================================================= */
export const createAdminStudent = async (payload, options = {}) => {
  try {
    const res = await apiCall(
      "POST",
      ADMIN_STUDENTS_ENDPOINT,
      payload,
      "Admin – Create Student",
      options
    );

    if (!res || res.status !== true) {
      return {
        success: false,
        message: res?.message || "Failed to create student",
      };
    }

    return {
      success: true,
      data: res.data,
      message: res.message,
    };
  } catch (error) {
    console.error("Admin Create Student Error:", error);

    return {
      success: false,
      message: error.message || "Unexpected error occurred",
    };
  }
};

/* =============================================================
   PUT – Update Student (Production Safe)
   Method: PUT
   URL: /admin/students/index.php?id=ID
============================================================= */
export const updateAdminStudent = async (
  id,
  payload,
  options = {}
) => {
  try {
    if (!id) {
      return {
        success: false,
        message: "Invalid student ID",
      };
    }

    const res = await apiCall(
      "PUT",
      `${ADMIN_STUDENTS_ENDPOINT}?id=${id}`,   // ✅ Query param based
      payload,
      "Admin – Update Student",
      options
    );

    if (!res || res.status !== true) {
      return {
        success: false,
        message: res?.message || "Failed to update student",
      };
    }

    return {
      success: true,
      data: res.data || null,
      message: res.message || "Student updated successfully",
    };

  } catch (error) {
    console.error("Admin Update Student Error:", error);

    return {
      success: false,
      message: error?.message || "Unexpected error occurred",
    };
  }
};


/* =============================================================
   DELETE – Remove Student
============================================================= */
export const deleteAdminStudent = async (id, options = {}) => {
  try {
    const res = await apiCall(
      "DELETE",
      `${ADMIN_STUDENTS_ENDPOINT}/${id}`,
      null,
      "Admin – Delete Student",
      options
    );

    if (!res || res.status !== true) {
      return {
        success: false,
        message: res?.message || "Failed to delete student",
      };
    }

    return {
      success: true,
      message: res.message,
    };
  } catch (error) {
    console.error("Admin Delete Student Error:", error);

    return {
      success: false,
      message: error.message || "Unexpected error occurred",
    };
  }
};
